/**
 * Utility functions for processing research query resource data
 */

import { apiType, sqlDatabaseType } from "../constants";

/**
 * Get API definition from resource
 * @param resource - The resource object
 * @returns The API definition
 */
export const getApiDefinition = (resource: any) => {
  let user_name =
    resource?.additional_properties?.resource_definition?.api_definition
      ?.user_name || null;
  let password =
    resource?.additional_properties?.resource_definition?.api_definition
      ?.password || null;
  let bearer_token =
    resource?.additional_properties?.resource_definition?.api_definition
      ?.bearer_token || null;
  let api_key =
    resource?.additional_properties?.resource_definition?.api_definition
      ?.api_key || null;
  let oauth_client_id =
    resource?.additional_properties?.resource_definition?.api_definition
      ?.oauth_client_id || null;
  let oauth_client_secret =
    resource?.additional_properties?.resource_definition?.api_definition
      ?.oauth_client_secret || null;
  let oauth_url =
    resource?.additional_properties?.resource_definition?.api_definition
      ?.oauth_url || null;
  let method =
    resource?.additional_properties?.resource_definition?.api_definition
      ?.method || "get";
  let content_type =
    resource?.additional_properties?.resource_definition?.api_definition
      ?.content_type || "application/json";
  let body =
    resource?.additional_properties?.resource_definition?.api_definition
      ?.body || null;
  let query_params =
    resource?.additional_properties?.resource_definition?.api_definition
      ?.query_params || null;
  let url_params =
    resource?.additional_properties?.resource_definition?.api_definition
      ?.url_params || null;
  let url =
    resource?.additional_properties?.resource_definition?.api_definition?.url ||
    null;
  let request_timeout =
    resource?.additional_properties?.resource_definition?.api_definition
      ?.request_timeout || 0;
  return {
    user_name,
    password,
    bearer_token,
    api_key,
    oauth_client_id,
    oauth_client_secret,
    oauth_url,
    method,
    content_type,
    body,
    query_params,
    url_params,
    url,
    request_timeout,
  };
};

/**
 * Get resource definition based on type
 * @param resource - The resource object
 * @param type - The resource type
 * @returns The resource definition
 */
export const getResourceDefinition = (resource: any, type: string) => {
  let connection_key = null;
  let container_name = null;
  let resource_path = null;
  let file_name = null;
  let column_delimiter = null;
  let sql_query = null;
  let use_multi_thread_reader = false;
  let column_name_to_partition_on_sql_query = null;
  const resourceDef = resource?.additional_properties?.resource_definition;

  if (type === "blob") {
    connection_key = resourceDef?.connection_key || null;
    container_name = resourceDef?.container_name || null;
    resource_path = resourceDef?.resource_path || null;
    file_name = resourceDef?.file_name || null;
    column_delimiter = resourceDef?.column_delimiter || null;
  } else if (type === "sftp") {
    connection_key = resourceDef?.connection_key || null;
    resource_path = resourceDef?.remote_directory || null;
    file_name = resourceDef?.file_name || null;
    column_delimiter = resourceDef?.column_delimiter || null;
  } else if (type === "local") {
    connection_key = resourceDef?.connection_key || null;
    resource_path = resourceDef?.resource_path || null;
    file_name = resourceDef?.file_name || null;
    column_delimiter = resourceDef?.column_delimiter || null;
  } else if (sqlDatabaseType.includes(type)) {
    sql_query = resourceDef?.sql_query || null;
    connection_key = resourceDef?.connection_key || null;
    use_multi_thread_reader =
      resourceDef?.sql_definition?.use_multi_thread_reader || false;
    column_name_to_partition_on_sql_query =
      resourceDef?.sql_definition?.column_name_to_partition_on_sql_query ||
      null;
  } else if (apiType.includes(type)) {
    connection_key = resourceDef?.api_definition?.connection_key || null;
  }

  return {
    connection_key,
    container_name,
    resource_path,
    file_name,
    column_delimiter,
    sql_query,
    use_multi_thread_reader,
    column_name_to_partition_on_sql_query,
  };
};

/**
 * Process additional resource data
 * @param additionalResourceData - The additional resource data
 * @returns Processed additional resource data
 */
export const processAdditionalResourceData = (additionalResourceData: any) => {
  if (!Array.isArray(additionalResourceData)) {
    return null;
  }
  const res = additionalResourceData
    .map((resource) => getResourceReqBody(resource))
    .filter(Boolean);
  return res;
};

/**
 * Get file pre-processing data
 * @param fileProcessingId - The file processing ID
 * @returns File pre-processing data
 */
export const getFilePreProcessing = (fileProcessingId: any) => {
  // This is a placeholder. In a real implementation, you would fetch the file processing data
  // based on the fileProcessingId or implement the logic to generate it.
  return fileProcessingId ? {} : null;
};

/**
 * Get resource request body
 * @param resource - The resource object
 * @returns The resource request body that matches the schema
 */
export const stripLegacyTypeField = <T extends Record<string, any>>(
  payload: T,
) => {
  if (!payload || typeof payload !== "object") {
    return payload;
  }

  const { type, ...rest } = payload;
  return rest as Omit<T, "type">;
};

export const getResourceReqBody = (resource: any) => {
  if (!resource) {
    return null;
  }

  const aggregationType = resource?.aggregation_type;
  const type = resource?.additional_properties?.resource_definition?.type;
  const resourcePrefix = resource?.resource_prefix || resource?.resource_code;
  const resourceType = type || "local";

  // Process additional resource data if available
  const additional_base_resource_data =
    resource?.additional_properties?.additional_resource_data?.length > 0
      ? processAdditionalResourceData(
          resource?.additional_properties?.additional_resource_data,
        )
      : null;

  // Get filter rules
  const filter_rules = resource?.additional_properties?.filter_rules || [];

  // Get resource definition based on type
  const {
    connection_key,
    container_name,
    resource_path,
    file_name,
    column_delimiter,
    sql_query,
    use_multi_thread_reader,
    column_name_to_partition_on_sql_query,
  } = getResourceDefinition(resource, resourceType);

  // Get API definition if applicable
  const {
    user_name,
    password,
    bearer_token,
    api_key,
    oauth_client_id,
    oauth_client_secret,
    oauth_url,
    method,
    content_type,
    body,
    query_params,
    url_params,
    url,
    request_timeout,
  } = getApiDefinition(resource);

  // Create API request object if applicable
  const api_request = apiType.includes(resourceType)
    ? {
        url,
        method,
        content_type,
        body,
        query_params,
        url_params,
        user_name,
        password,
        bearer_token,
        api_key,
        oauth_client_id,
        oauth_client_secret,
        oauth_url,
        request_timeout,
      }
    : null;

  // Get file pre-processing if applicable
  const file_pre_processing =
    sqlDatabaseType.includes(resourceType) || apiType.includes(resourceType)
      ? null
      : getFilePreProcessing(resource?.file_processing_id);

  // Get secondary merge resource if available
  const secondary_merge_resource = resource?.secondary_merge_resource
    ? {
        resource_id: resource.secondary_merge_resource.resource_id,
        resource_code: resource.secondary_merge_resource.resource_code,
        merge_on: resource.secondary_merge_resource.merge_on || [],
      }
    : null;

  // Get pre-processing request if available
  const pre_processing_request = resource?.pre_processing_request || null;

  // Get secondary merge resource parent ID if available
  const secondary_merge_resource_parent_id =
    resource?.secondary_merge_resource_parent_id ||
    resource?.isSecondaryResource
      ? resource?.parentResId
      : null;

  // Get excel sheet name if available
  const excel_sheet_name = resource?.excel_sheet_name || null;

  // Create aggregation properties if applicable
  const aggregation_properties =
    aggregationType === "aggregated"
      ? {
          base_resource_id:
            resource?.additional_properties?.aggregation_properties
              ?.base_resource_id,
          base_resource_code:
            resource?.additional_properties?.aggregation_properties
              ?.base_resource_code,
          aggregation_query:
            resource?.additional_properties?.aggregation_properties
              ?.aggregation_query,
          base_resource_columns:
            resource?.additional_properties?.aggregation_properties
              ?.base_resource_columns,
          base_resource_validation:
            resource?.additional_properties?.aggregation_properties
              ?.base_resource_validation,
        }
      : null;

  // Create aggregation base resource data if applicable
  const aggregation_base_resource_data =
    aggregationType === "aggregated"
      ? getResourceReqBody(
          resource?.additional_properties?.aggregation_properties,
        )
      : null;

  // Build the resource object according to the schema
  let resourceObject: any = {
    resource_id: resource?.id || resource?.resource_id,
    resource_prefix: resourcePrefix,
    resource_type: resourceType,
    resource_name: resource?.resource_name,
    aggregation_type: aggregationType,
    resource_column_details_id:
      resource?.additional_properties?.resource_column_details_id,
    file_processing_id: resource?.file_processing_id,
    linked_service_id: resource?.linked_service_id,
    linked_service_code: resource?.linked_service_code,
    connection_key,
    sql_query,
    use_multi_thread_reader,
    column_name_to_partition_on_sql_query,
    container_name,
    file_name,
    column_delimiter,
    file_pre_processing,
    filter_rules,
    api_request,
    aggregation_properties,
    aggregation_base_resource_data,
    additional_base_resource_data,
    secondary_merge_resource,
    pre_processing_request,
    secondary_merge_resource_parent_id,
    excel_sheet_name,
  };

  // The execution API uses resource_path for every resource type, including SFTP.
  if (resource_path) {
    resourceObject.resource_path = resource_path;
  }

  // Remove null/undefined values
  resourceObject = Object.fromEntries(
    Object.entries(resourceObject).filter(
      ([_, v]) => v !== null && v !== undefined,
    ),
  );

  return Object.keys(resourceObject).length
    ? stripLegacyTypeField(resourceObject)
    : null;
};

const getReRunResourceDefinition = (resource: any) => {
  const resourceType = resource?.type || resource?.resource_type || null;
  let connection_key = resource?.connection_key || null;
  let container_name = resource?.container_name || null;
  let resource_path =
    resourceType === "sftp"
      ? resource?.remote_directory || resource?.resource_path || null
      : resource?.resource_path || null;
  let file_name = resource?.file_name || null;
  let excel_sheet_name = resource?.excel_sheet_name || null;
  let column_delimiter = resource?.column_delimiter || null;
  let sql_query = resource?.sql_query || null;
  let use_multi_thread_reader = resource?.use_multi_thread_reader || false;
  let column_name_to_partition_on_sql_query =
    resource?.column_name_to_partition_on_sql_query || null;
  let use_parquet_export = resource?.use_parquet_export || false;
  let parquet_export_definition = use_parquet_export
    ? resource?.parquet_export_definition || null
    : null;

  return {
    connection_key,
    container_name,
    resource_path,
    file_name,
    excel_sheet_name,
    column_delimiter,
    sql_query,
    use_multi_thread_reader,
    column_name_to_partition_on_sql_query,
    use_parquet_export,
    parquet_export_definition,
  };
};

const processReRunAdditionalResourceData = (additionalResourceData: any) => {
  if (!Array.isArray(additionalResourceData)) {
    return null;
  }
  const res = additionalResourceData
    .map((resource) => getAdditionalResourceData(resource))
    .filter(Boolean);
  return res;
};

const buildResourceObject = (
  resource: any,
  options: {
    handleAggregationBaseResource?: boolean;
    removeNullValues?: boolean;
    minKeyCount?: number;
  } = {},
) => {
  const {
    handleAggregationBaseResource = false,
    removeNullValues = false,
    minKeyCount = 1,
  } = options;

  const type = resource?.type || resource?.resource_type;

  const {
    connection_key,
    container_name,
    resource_path,
    file_name,
    excel_sheet_name,
    column_delimiter,
    sql_query,
    use_multi_thread_reader,
    column_name_to_partition_on_sql_query,
    use_parquet_export,
    parquet_export_definition,
  } = getReRunResourceDefinition(resource);

  const {
    user_name,
    password,
    bearer_token,
    api_key,
    oauth_client_id,
    oauth_client_secret,
    oauth_url,
    method,
    content_type,
    body,
    query_params,
    url_params,
    url,
    request_timeout,
  } = getApiDefinition(resource);

  const additional_base_resource_data = resource?.additional_base_resource_data
    ? processReRunAdditionalResourceData(
        resource?.additional_base_resource_data,
      )
    : null;

  let resourceObject: any = {
    aggregation_type: resource?.aggregation_type,
    resource_name: resource?.resource_name,
    resource_column_details_id:
      resource?.additional_properties?.resource_column_details_id,
    file_processing_id: resource?.file_processing_id,
    resource_id: resource?.resource_id || null,
    linked_service_id: resource?.linked_service_id || null,
    linked_service_code: resource?.linked_service_code || null,
    connection_key,
    sql_query,
    container_name,
    file_name,
    excel_sheet_name,
    column_delimiter,
    use_multi_thread_reader,
    column_name_to_partition_on_sql_query,
    use_parquet_export,
    parquet_export_definition,
    additional_base_resource_data,
    filter_rules: resource?.filter_rules,
    file_pre_processing:
      sqlDatabaseType.includes(type) || apiType.includes(type)
        ? null
        : getFilePreProcessing(resource?.file_processing_id),
    aggregation_properties:
      resource?.aggregation_type === "flat"
        ? null
        : resource?.aggregation_properties || null,
    aggregation_base_resource_data: handleAggregationBaseResource
      ? resource?.aggregation_type === "flat"
        ? null
        : getAdditionalResourceData(resource?.aggregation_base_resource_data) ||
          null
      : null,
    api_request: {
      user_name,
      password,
      bearer_token,
      api_key,
      oauth_client_id,
      oauth_client_secret,
      oauth_url,
      method,
      content_type,
      body,
      query_params,
      url_params,
      url,
      request_timeout,
    },
  };

  // Handle resource path - backend now expects resource_path for all types including SFTP
  // (remote_directory was the legacy key; resource_path is the unified key per updated API contract)
  if (resource_path) {
    resourceObject.resource_path = resource_path;
  }

  // Remove null/undefined values if requested
  if (removeNullValues) {
    resourceObject = Object.fromEntries(
      Object.entries(resourceObject).filter(
        ([_, v]) => v !== null && v !== undefined,
      ),
    );
  }

  // Check if object meets minimum requirements
  if (
    Object.keys(resourceObject).length === 0 &&
    resourceObject.constructor === Object
  ) {
    return null;
  }

  return Object.keys(resourceObject).length > minKeyCount
    ? resourceObject
    : null;
};

const getAdditionalResourceData = (resourceData: any) => {
  const resource: any = resourceData;
  return buildResourceObject(resource, {
    handleAggregationBaseResource: false,
    removeNullValues: false,
    minKeyCount: 1,
  });
};

export const getReRunResourceReqBody = (resource: any) => {
  if (!resource) {
    return null;
  }

  return buildResourceObject(resource, {
    handleAggregationBaseResource: true,
    removeNullValues: true,
    minKeyCount: 0,
  });
};
